import { useState } from 'react';
import { Link } from 'wouter';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useGetClaimExpenditure, customInstance } from '@edison/api-client-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bar } from '@/components/ui/readiness-row.js';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { ClaimModeBadge } from '@/components/claim-mode-badge.js';
import { CLAIM_MODE_META, CLAIM_MODES, type ClaimMode } from '@/store/claim-mode-store.js';

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(n);
}

interface Props {
  claimId: string;
  claimStatus: string;
  claimMode?: string | null;
  onModeChange?: () => void;
}

function PendingCostSection({ claimId }: { claimId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['claim-pending-costs', claimId],
    queryFn: async () => {
      const res = await customInstance<{ data: { items: Array<{ id: string; source: string; description: string | null; amount: string }> } }>({
        url: `/api/cost-allocations?claimId=${claimId}&unassigned=true`,
        method: 'GET',
      });
      return res.data;
    },
  });

  const items = data?.items ?? [];
  if (isLoading || items.length === 0) return null;

  return (
    <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-300">
      <AlertCircle className="inline h-3 w-3 mr-1" aria-hidden="true" />
      {items.length} non-labour cost item{items.length > 1 ? 's' : ''} pending attribution.{' '}
      <Link href="/expenditure" className="underline font-medium">Review in Expenditure →</Link>
    </div>
  );
}

export function ClaimExpenditureTab({ claimId, claimStatus, claimMode, onModeChange }: Props) {
  const { data: expenditureData, isLoading, refetch } = useGetClaimExpenditure(claimId);
  const queryClient = useQueryClient();
  const [savingMode, setSavingMode] = useState(false);
  const [retagging, setRetagging] = useState(false);
  const [retagResult, setRetagResult] = useState<{ propagated: number } | null>(null);

  async function handleModeChange(newMode: ClaimMode) {
    setSavingMode(true);
    try {
      await customInstance({ url: `/api/claims/${claimId}`, method: 'PATCH', data: { claimMode: newMode } });
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ['getClaimExpenditure', claimId] });
      onModeChange?.();
    } finally {
      setSavingMode(false);
    }
  }

  async function handleRetag() {
    setRetagging(true);
    setRetagResult(null);
    try {
      const res = await customInstance<{ data: { propagated: number } }>({
        url: `/api/claims/${claimId}/retag-time-entries`,
        method: 'POST',
      });
      setRetagResult(res.data);
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ['claim-expenditure', claimId] });
    } catch {
      // surface nothing — the button state resets
    } finally {
      setRetagging(false);
    }
  }
  const expenditure = expenditureData?.data as {
    mode?: string;
    labour?: {
      totalRdHours?: number;
      totalRdExpenditure?: number;
      approvedEntryCount?: number;
      byProject?: Array<{ projectId: string; name?: string | null; rdHours: number; rdExpenditure: number; entryCount: number }>;
    };
    nonLabour?: {
      total?: number;
      items?: Array<{ id: string; description?: string | null; category: string; amount: string }>;
    };
    totalExpenditure?: number;
    missingRates?: Array<{ userId: string; name?: string | null }>;
  } | undefined;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 rounded-lg skeleton" />
        ))}
      </div>
    );
  }

  const byProject = (expenditure?.labour?.byProject ?? []) as Array<{ projectId: string; name?: string | null; rdHours: number; rdExpenditure: number; entryCount: number }>;
  const maxProjectExp = Math.max(...byProject.map((p) => p.rdExpenditure), 1);

  return (
    <div className="space-y-6">
      {/* Missing rates warning */}
      {(expenditure?.missingRates?.length ?? 0) > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-300">
          <AlertCircle className="inline h-3 w-3 mr-1" aria-hidden="true" />
          {expenditure!.missingRates!.length} staff member{expenditure!.missingRates!.length > 1 ? 's' : ''} have no rate card — their time is excluded from the estimate.{' '}
          Set rates in <Link href="/users" className="underline">Users → Rate Card</Link>.
        </div>
      )}

      <PendingCostSection claimId={claimId} />

      {/* Summary card */}
      <Card variant="static" className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-medium text-sm">R&D Expenditure Estimate</h2>
          {claimStatus === 'locked' ? (
            <ClaimModeBadge mode={expenditure?.mode} locked />
          ) : (
            <div className="flex items-center gap-2">
              <label htmlFor="claim-mode-select" className="text-xs text-muted-foreground">R&D basis</label>
              <select
                id="claim-mode-select"
                className="text-xs border rounded px-2 py-1 bg-background"
                value={claimMode ?? expenditure?.mode ?? 'business_hours'}
                disabled={savingMode}
                onChange={(e) => { void handleModeChange(e.target.value as ClaimMode); }}
              >
                {CLAIM_MODES.map((m) => (
                  <option key={m} value={m}>{CLAIM_MODE_META[m].label}</option>
                ))}
              </select>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { void handleRetag(); }}
                disabled={retagging}
                title="Propagate activity tags from evidence to time entries"
              >
                <RefreshCw className={`h-3 w-3 mr-1.5 ${retagging ? 'animate-spin' : ''}`} aria-hidden="true" />
                {retagResult !== null ? `${retagResult.propagated} re-tagged` : 'Re-tag entries'}
              </Button>
            </div>
          )}
        </div>

        {/* Metric grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Approved entries</p>
            <p className="text-2xl font-semibold font-mono tabular-nums">{expenditure?.labour?.approvedEntryCount ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">R&D hours</p>
            <p className="text-2xl font-semibold font-mono tabular-nums">{(expenditure?.labour?.totalRdHours ?? 0).toFixed(1)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Labour R&D expenditure</p>
            <p className="text-2xl font-semibold font-mono tabular-nums">{formatCurrency(expenditure?.labour?.totalRdExpenditure ?? 0)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Total (incl. non-labour)</p>
            <p className="text-2xl font-semibold font-mono tabular-nums text-primary">{formatCurrency(expenditure?.totalExpenditure ?? 0)}</p>
          </div>
        </div>

        {/* Labour by project bars */}
        {byProject.length > 0 && (
          <div className="border-t pt-4 space-y-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Labour by project</p>
            {byProject.map((row) => (
              <Bar
                key={row.projectId}
                label={row.name ?? row.projectId}
                value={formatCurrency(row.rdExpenditure)}
                pct={Math.round((row.rdExpenditure / maxProjectExp) * 100)}
                tone="indigo"
              />
            ))}
          </div>
        )}

        {/* Non-labour costs */}
        {(expenditure?.nonLabour?.items?.length ?? 0) > 0 && (
          <div className="border-t pt-4 space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Non-labour costs</p>
            {expenditure!.nonLabour!.items!.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-sm py-0.5">
                <span className="text-muted-foreground">{item.description ?? item.category}</span>
                <span className="font-mono tabular-nums">{formatCurrency(parseFloat(item.amount))}</span>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm font-medium pt-1 border-t">
              <span>Non-labour total</span>
              <span className="font-mono tabular-nums">{formatCurrency(expenditure?.nonLabour?.total ?? 0)}</span>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
